import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { Bar } from 'react-chartjs-2';
import { MapPin } from '@phosphor-icons/react';
import { formatCurrency } from 'shared/utils/currency';
import { AcquisitionRegionBucket } from '../types/statistics.types';
import { CHART_COLORS, CHART_COLORS_ALPHA, COMMON_TOOLTIP } from './chartConfig';
import { MetricInfo } from './MetricInfo';
import './chartConfig';

interface Props {
  data?: AcquisitionRegionBucket[];
  loading: boolean;
}

export const RegionDistributionChart = ({ data, loading }: Props) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    const sorted = [...data].sort((a, b) => b.count - a.count);
    return {
      labels: sorted.map((d) => d.province),
      datasets: [
        {
          label: 'Dossiers',
          data: sorted.map((d) => d.count),
          backgroundColor: CHART_COLORS_ALPHA.slice(0, sorted.length),
          borderColor: CHART_COLORS.slice(0, sorted.length),
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
      _meta: sorted,
    };
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-moss-primary" weight="duotone" />
            <Skeleton className="h-5 w-40" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[320px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-moss-primary" weight="duotone" />
          <CardTitle className="text-lg">Dossiers per regio</CardTitle>
          <MetricInfo
            description={
              <div className="space-y-1">
                <p>
                  Aantal dossiers per provincie. De provincie wordt afgeleid uit
                  de Belgische postcode in het{' '}
                  <code>googlePlaceLongAddress</code> van de gekoppelde
                  gebouwen (bv. 2000 → Antwerpen, 9000 → Oost-Vlaanderen).
                </p>
                <p>
                  Een dossier met meerdere gebouwen in verschillende provincies
                  telt mee in elke provincie. Volume wordt evenredig verdeeld
                  zodat de totaalsom per dossier hetzelfde blijft.
                </p>
                <p>
                  Dossiers zonder bruikbare postcode (en zonder ingevulde
                  <code> city.province</code> als fallback) komen onder
                  "Onbekend".
                </p>
              </div>
            }
          />
        </div>
        <p className="text-xs text-neutral-secondary-mid">
          Provincie afgeleid uit Belgische postcode (uit googlePlaceLongAddress)
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[320px]">
          {chartData ? (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y' as const,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    ...COMMON_TOOLTIP,
                    callbacks: {
                      label: (ctx: any) => {
                        const meta = chartData._meta[ctx.dataIndex];
                        const conv =
                          meta.deals + meta.rejected > 0
                            ? (meta.deals / (meta.deals + meta.rejected)) * 100
                            : 0;
                        const lines = [
                          `${meta.count} dossiers`,
                          `${meta.deals} deals · ${meta.rejected} afgewezen`,
                          `Conversie: ${conv.toFixed(0)}%`,
                          `Volume: ${formatCurrency(meta.totalVolume)}`,
                        ];
                        if (meta.dealVolume > 0) {
                          lines.push(
                            `Volume gewonnen: ${formatCurrency(meta.dealVolume)}`,
                          );
                        }
                        if (meta.dealYieldSampleSize > 0) {
                          lines.push(
                            `Gem. yield deals: ${(meta.avgDealYield * 100).toFixed(2)}%`,
                          );
                          lines.push(
                            `Volume-gew. yield: ${(meta.volumeWeightedDealYield * 100).toFixed(2)}%`,
                          );
                        }
                        return lines;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    beginAtZero: true,
                    grid: { color: 'rgba(49, 64, 59, 0.1)' },
                    ticks: { precision: 0 },
                  },
                  y: { grid: { display: false } },
                },
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-secondary-mid">
              Geen data beschikbaar
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
